import { useMemo, useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  MessageCircle,
  RefreshCw,
  Search,
  Smartphone,
} from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { useClients } from '../../hooks/useClients';
import {
  useLinkWhatsAppGroup,
  useRequestWhatsAppQr,
  useStartWhatsAppSync,
  useWhatsAppGroups,
  useWhatsAppStatus,
} from '../../hooks/useWhatsApp';
import { Select } from '../ui/Select';

/**
 * Setări → Integrări → WhatsApp, under the standard card that holds the
 * sidecar address and token (EfacturaCard pattern; its description carries the
 * unofficial-connection warning): the session state with the pairing QR code,
 * and the groups of the linked number with the client each one belongs to —
 * folded by default (a number can be in dozens of groups), linked ones first.
 * Only linked groups sync.
 */
export function WhatsAppCard() {
  const { t, locale } = useI18n();
  const status = useWhatsAppStatus();
  const s = status.data;
  const connected = Boolean(s?.connection?.connected);
  const groups = useWhatsAppGroups(Boolean(s?.configured));
  const clients = useClients({ active: 'true', sort: 'name', limit: 100 });
  const link = useLinkWhatsAppGroup();
  const sync = useStartWhatsAppSync();
  const newQr = useRequestWhatsAppQr();
  const [started, setStarted] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const allGroups = useMemo(() => groups.data ?? [], [groups.data]);
  const linkedCount = allGroups.filter((g) => g.clientId).length;
  // Linked groups first, then the rest in the API order (most recent first).
  const shownGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q ? allGroups.filter((g) => g.name.toLowerCase().includes(q)) : allGroups;
    return [...rows.filter((g) => g.clientId), ...rows.filter((g) => !g.clientId)];
  }, [allGroups, search]);

  const when = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });
  const clientOptions = (clients.data?.data ?? []).map((c) => ({ value: c.id, label: c.name }));

  const stateLabel = connected
    ? t('whatsapp.settings.connected')
    : s?.connection?.state === 'pairing'
      ? t('whatsapp.settings.pairing')
      : t('whatsapp.settings.disconnected');

  const last = s?.lastSync;

  return (
    <section className="rounded-lg border border-gray-200 bg-card p-5">
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-green-600/15 p-2 text-green-600">
          <MessageCircle className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900">{t('whatsapp.settings.title')}</h3>
            {s?.configured && s.connection && (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                  connected ? 'bg-green-500/15 text-green-700' : 'bg-amber-500/15 text-amber-700'
                }`}
              >
                {connected ? <CheckCircle2 size={12} /> : <Smartphone size={12} />}
                {stateLabel}
              </span>
            )}
          </div>

          {status.isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          ) : !s?.configured ? (
            <p className="text-sm text-gray-500">{t('whatsapp.settings.notConfigured')}</p>
          ) : s.error ? (
            <p className="text-sm text-red-600">
              {t('whatsapp.settings.unreachable', { error: s.error })}
            </p>
          ) : connected ? null : s.qrDataUrl ? (
            <div className="flex flex-wrap items-start gap-4">
              <img
                src={s.qrDataUrl}
                alt="QR WhatsApp"
                className="h-56 w-56 rounded-md border border-gray-200 bg-white p-2"
              />
              <p className="max-w-sm text-sm text-gray-600">{t('whatsapp.settings.scanHelp')}</p>
            </div>
          ) : (
            // The QR codes expired (WhatsApp hands out a few for ~2-3 minutes)
            // or the session dropped: ask for a new round.
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-gray-600">
                {s.connection?.state === 'connecting'
                  ? t('whatsapp.settings.connecting')
                  : t('whatsapp.settings.qrExpired')}
              </p>
              <button
                type="button"
                disabled={newQr.isPending || s.connection?.state === 'connecting'}
                onClick={() => newQr.mutate()}
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                <RefreshCw size={14} className={newQr.isPending ? 'animate-spin' : ''} />
                {t('whatsapp.settings.newQr')}
              </button>
            </div>
          )}

          {s?.configured && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    className="inline-flex items-center gap-1 text-sm font-medium text-gray-900 hover:text-brand-600"
                  >
                    {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    {t('whatsapp.settings.groups')}
                    {allGroups.length > 0 && (
                      <span className="ml-1 text-xs font-normal text-gray-500">
                        {t('whatsapp.settings.groupsCount', {
                          linked: linkedCount,
                          total: allGroups.length,
                        })}
                      </span>
                    )}
                  </button>
                  <p className="text-xs text-gray-500">
                    {t('whatsapp.settings.groupsHelp', { hours: s.autoSync.intervalHours })}
                    {!s.autoSync.enabled && ` ${t('whatsapp.settings.autoOff')}`}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={!connected || sync.isPending}
                  onClick={() => sync.mutate(undefined, { onSuccess: () => setStarted(true) })}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <RefreshCw size={14} className={sync.isPending ? 'animate-spin' : ''} />
                  {t('whatsapp.settings.syncNow')}
                </button>
              </div>

              {started && (
                <p className="text-xs text-green-700">{t('whatsapp.settings.syncStarted')}</p>
              )}
              {last && (
                <p className="text-xs text-gray-500">
                  {last.status === 'ok'
                    ? t('whatsapp.settings.lastSync', {
                        when: when.format(new Date(last.at)),
                        created: last.created,
                        synced: last.synced,
                      })
                    : t('whatsapp.settings.lastSyncStatus', {
                        when: when.format(new Date(last.at)),
                        message: last.message ?? last.status,
                      })}
                </p>
              )}

              {!open ? null : groups.data && groups.data.length === 0 ? (
                <p className="rounded-md border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">
                  {t('whatsapp.settings.noGroups')}
                </p>
              ) : (
                <div className="space-y-2">
                  {allGroups.length > 8 && (
                    <div className="relative w-full sm:w-72">
                      <Search
                        size={14}
                        className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('whatsapp.settings.searchGroups')}
                        className="ui-input h-8 pl-8 text-sm"
                      />
                    </div>
                  )}
                  <div className="max-h-[28rem] overflow-auto rounded-md border border-gray-200">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-left text-xs text-gray-500">
                        <tr>
                          <th className="px-3 py-2 font-medium">
                            {t('whatsapp.settings.col.group')}
                          </th>
                          <th className="px-3 py-2 font-medium">
                            {t('whatsapp.settings.col.client')}
                          </th>
                          <th className="px-3 py-2 text-right font-medium">
                            {t('whatsapp.settings.col.messages')}
                          </th>
                          <th className="px-3 py-2 font-medium">
                            {t('whatsapp.settings.col.synced')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {shownGroups.map((g) => (
                          <tr key={g.id}>
                            <td className="px-3 py-2">
                              <span className="font-medium text-gray-900">{g.name}</span>
                              {g.lastError && (
                                <span className="block text-xs text-red-600">{g.lastError}</span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <Select
                                label={t('whatsapp.settings.col.client')}
                                hideLabel
                                size="sm"
                                searchable
                                clearable
                                value={g.clientId ?? ''}
                                placeholder={t('whatsapp.settings.noClient')}
                                options={clientOptions}
                                disabled={link.isPending}
                                onChange={(clientId) =>
                                  link.mutate({ groupId: g.id, clientId: clientId || null })
                                }
                                width={240}
                              />
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                              {g.messageCount}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-500">
                              {g.lastSyncedAt ? when.format(new Date(g.lastSyncedAt)) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
